import type {IImmutableStep} from '#lit/components/base/immutable_stepper/immutable_stepper/architecture.js';
import type {AudienceDatasetIds} from '#lit/contexts/audience.js';
import {Channel} from '#utils/channel.js';
import type {Validator} from '#lit/lib/validator.js';
import type {Audience, AudienceExportFormData} from '#lit/types/audiences.js';
import type {
  AudienceExport,
  ExportConfigFormValues,
} from '#lit/types/audience_exports.js';
import type {DestinationConnection} from '#types/destinations.js';
import type {AnyObject, Nullable} from '#lit/types/general.js';
import {ChannelController} from '../state_controller/index.js';
import {
  on,
  provide,
  stateController,
} from '../state_controller/state_controller_decorators.js';
import type {IngestSource, Platform} from '#types/ingest.js';
import {audienceStore} from '#stores/audience/index.js';

const audienceExportChannel = Channel.create('ae:audience-export-channel');

export enum AudienceExportControllerActions {
  SET_SELECTED_DESTINATION = 'ae:set-selected-destination',
  UPDATE_FORM_VALUES = 'ae:update-form-values',
  OPEN_MODAL = 'ae:open-modal',
  CLOSE_MODAL = 'ae:close-modal',
  NEXT = 'ae:next',
  PREVIOUS = 'ae:previous',
  SET_STEPS = 'ae:set-steps',
  PREPARE_EXPORT = 'ae:prepare-export',
  PREPARE_DOWNLOAD = 'ae:prepare-download',
  EXPORT_READY = 'ae:export-ready',
  DOWNLOAD_READY = 'ae:download-ready',
  EXPORT = 'ae:export',
  VALIDATE = 'ae:validate',
  SET_EXPORTS = 'ae:set-exports',
  SET_INGESTS = 'ae:set-ingests',
  SET_PLATFORMS = 'ae:set-platforms',
  SHARE_RESULTS = 'ae:share-results',
}

export type ExportServiceParams = {
  audienceId: AudienceDatasetIds;
  formValues: ExportConfigFormValues;
  destination: DestinationConnection;
  audienceSize?: number;
};

type ValidationParams = {
  step: string;
  formValues: AnyObject;
  validator: Validator<unknown, string>;
};

type ShareResultsParams = {
  destinationCriteria: string;
  reportName: string;
};

export type AudienceExportControllerActionMap = {
  [AudienceExportControllerActions.SET_SELECTED_DESTINATION]: Nullable<DestinationConnection>;
  [AudienceExportControllerActions.OPEN_MODAL]: void;
  [AudienceExportControllerActions.CLOSE_MODAL]: void;
  [AudienceExportControllerActions.UPDATE_FORM_VALUES]: Partial<Audience>;
  [AudienceExportControllerActions.NEXT]: void;
  [AudienceExportControllerActions.PREVIOUS]: void;
  [AudienceExportControllerActions.SET_STEPS]: IImmutableStep[];
  [AudienceExportControllerActions.EXPORT]: ExportServiceParams;
  [AudienceExportControllerActions.EXPORT_READY]: void;
  [AudienceExportControllerActions.DOWNLOAD_READY]: void;
  [AudienceExportControllerActions.PREPARE_EXPORT]: void;
  [AudienceExportControllerActions.PREPARE_DOWNLOAD]: void;
  [AudienceExportControllerActions.VALIDATE]: ValidationParams;
  [AudienceExportControllerActions.SET_EXPORTS]: AudienceExport[];
  [AudienceExportControllerActions.SET_INGESTS]: IngestSource[];
  [AudienceExportControllerActions.SET_PLATFORMS]: Platform[];
  [AudienceExportControllerActions.SHARE_RESULTS]: ShareResultsParams;
};

export type ProvideActionMap = {
  selectedDestination: Nullable<DestinationConnection>;
  formValues: {
    formValues: ExportConfigFormValues;
  };
  currentStep: number;
  numSteps: number;
  isValid: boolean;
  stepValidity: Record<string, boolean>;
  canExport: boolean;
  exports: AudienceExport[];
  ingests: IngestSource[];
  platforms: Platform[];
};

export type AudienceExportController = typeof audienceExportController;

export const audienceExportController = new ChannelController<
  AudienceExportControllerActionMap,
  ProvideActionMap
>(audienceExportChannel);

@stateController(audienceExportController)
export class AudienceExportStore {
  controller!: AudienceExportController;

  @provide<AudienceExportController>('numSteps')
  numSteps = 0;

  @provide<AudienceExportController>('currentStep')
  currentStep = 0;

  @provide<AudienceExportController>('selectedDestination')
  selectedDestination: Nullable<DestinationConnection> = null;

  @provide<AudienceExportController>('formValues')
  formValues = {};

  @provide<AudienceExportController>('isValid')
  isValid = false;

  @provide<AudienceExportController>('canExport')
  canExport = false;

  @provide<AudienceExportController>('stepValidity')
  stepValidity = {};

  @provide<AudienceExportController>('exports')
  exports: AudienceExport[] = [];

  @provide<AudienceExportController>('ingests')
  ingests: IngestSource[] = [];

  @provide<AudienceExportController>('platforms')
  platforms: Platform[] = [];

  @on<AudienceExportController>(
    AudienceExportControllerActions.SET_SELECTED_DESTINATION
  )
  setSelectedDestination(destination: DestinationConnection) {
    this.selectedDestination = destination;
  }

  @on<AudienceExportController>(
    AudienceExportControllerActions.UPDATE_FORM_VALUES
  )
  updateExportConfig(formValues: Partial<AudienceExportFormData>) {
    this.formValues = {
      ...this.formValues,
      formValues,
    };
  }

  @on<AudienceExportController>(AudienceExportControllerActions.SET_STEPS)
  setSteps(steps: IImmutableStep[]) {
    this.numSteps = steps.length;
  }

  @on<AudienceExportController>(AudienceExportControllerActions.VALIDATE)
  validate(params: ValidationParams) {
    const {step, formValues, validator} = params;
    this.isValid = validator.validate(formValues).valid;

    (this.stepValidity as Record<string, boolean>)[step] = this.isValid;

    this.canExport = this.checkValidity(this.stepValidity);
  }

  @on<AudienceExportController>(AudienceExportControllerActions.SET_EXPORTS)
  setExports(exports: AudienceExport[]) {
    this.exports = exports;
  }

  @on<AudienceExportController>(AudienceExportControllerActions.SET_INGESTS)
  setIngests(ingests: IngestSource[]) {
    this.ingests = ingests;
    audienceStore.ingests.getState().setIngests(ingests);
  }

  @on<AudienceExportController>(AudienceExportControllerActions.SET_PLATFORMS)
  setPlatforms(platforms: Platform[]) {
    this.platforms = platforms;
    audienceStore.ingests.getState().setPlatforms(platforms);
  }

  checkValidity(stepValidity: Record<string, boolean>) {
    const steps = Object.values(stepValidity);
    if (steps.length === 0) return true;
    for (let i = 0; i < steps.length; i++) {
      if (steps[i] === false) return false;
    }
    return true;
  }
}

export const store = new AudienceExportStore();
